import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import { registerBoard } from '@/features/class/classRegistry';

/**
 * A single class entry from the MyU login response.
 *
 * MyU returns classes under `boardWithCategory: [{ categoryId, categoryName,
 * boards: [...] }]`. Each `board` carries a `role` field
 * (`"owner" | "admin" | "member"`) and a `position` index for sort order.
 */
export interface OwnedBoard {
  id: number;
  code: string;
  name: string;
  role?: string;
  position?: number;
  categoryId?: number | null;
  categoryName?: string | null;
  classImage?: string;
  classColor?: string;
  /** Institution-controlled grade permissions supplied on each login board. */
  gradePerm?: Record<string, unknown>;
}

interface RawBoard {
  id: number | string;
  code: string;
  name: string;
  role?: string;
  position?: number;
  classImage?: string;
  classColor?: string;
  gradePerm?: unknown;
  gradePermission?: unknown;
}

function isRawBoard(value: unknown): value is RawBoard {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (typeof v.id === 'number' || typeof v.id === 'string') &&
    typeof v.code === 'string' &&
    typeof v.name === 'string'
  );
}

function toOwnedBoard(
  raw: RawBoard,
  categoryId: number | null,
  categoryName: string | null,
): OwnedBoard {
  const id = typeof raw.id === 'string' ? Number(raw.id) : raw.id;
  const board: OwnedBoard = {
    id,
    code: raw.code,
    name: raw.name,
    role: typeof raw.role === 'string' ? raw.role : undefined,
    position: typeof raw.position === 'number' ? raw.position : undefined,
    categoryId,
    categoryName,
    classImage: typeof raw.classImage === 'string' ? raw.classImage : undefined,
    classColor: typeof raw.classColor === 'string' ? raw.classColor : undefined,
    gradePerm:
      raw.gradePerm && typeof raw.gradePerm === 'object'
        ? (raw.gradePerm as Record<string, unknown>)
        : raw.gradePermission && typeof raw.gradePermission === 'object'
          ? (raw.gradePermission as Record<string, unknown>)
          : undefined,
  };
  registerBoard({ id: String(board.id), code: board.code, name: board.name });
  return board;
}

/**
 * Flatten `boardWithCategory[].boards[]` into a single list. Falls back to
 * the legacy flat `board[]` array when `boardWithCategory` is missing.
 *
 * The backend can list the same board in more than one group (e.g. its named
 * category AND the uncategorised bucket), so we deduplicate by board id — the
 * first occurrence wins — to avoid the same class appearing multiple times.
 */
function extractAllBoards(rawUser: unknown): OwnedBoard[] {
  if (!rawUser || typeof rawUser !== 'object') return [];
  const obj = rawUser as Record<string, unknown>;

  const categorized = obj.boardWithCategory;
  if (Array.isArray(categorized) && categorized.length > 0) {
    const out: OwnedBoard[] = [];
    const seen = new Set<string>();
    for (const group of categorized) {
      if (!group || typeof group !== 'object') continue;
      const g = group as Record<string, unknown>;
      const categoryId = typeof g.categoryId === 'number' ? g.categoryId : null;
      const categoryName = typeof g.categoryName === 'string' ? g.categoryName : null;
      const boards = g.boards;
      if (!Array.isArray(boards)) continue;
      for (const b of boards) {
        if (!isRawBoard(b)) continue;
        const key = String(b.id);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(toOwnedBoard(b, categoryId, categoryName));
      }
    }
    if (out.length > 0) return out;
  }

  const flat = obj.board;
  if (Array.isArray(flat) && flat.length > 0) {
    const seen = new Set<string>();
    return flat
      .filter(isRawBoard)
      .filter((b) => {
        const key = String(b.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((b) => toOwnedBoard(b, null, null));
  }

  return [];
}

function sortByPosition(a: OwnedBoard, b: OwnedBoard): number {
  const ap = a.position ?? Number.MAX_SAFE_INTEGER;
  const bp = b.position ?? Number.MAX_SAFE_INTEGER;
  if (ap !== bp) return ap - bp;
  return a.name.localeCompare(b.name);
}

const isOwnerRole = (role?: string) => (role ?? '').toLowerCase() === 'owner';

/** Non-React selector — owned classes (`role === "owner"`). */
export function getOwnedBoards(): OwnedBoard[] {
  return extractAllBoards(useAuthStore.getState().rawUser)
    .filter((b) => isOwnerRole(b.role))
    .sort(sortByPosition);
}

/** React hook — owned classes; re-renders when the login payload changes. */
export function useOwnedBoards(): OwnedBoard[] {
  const rawUser = useAuthStore((state) => state.rawUser);
  return useMemo(
    () =>
      extractAllBoards(rawUser)
        .filter((b) => isOwnerRole(b.role))
        .sort(sortByPosition),
    [rawUser],
  );
}

/** React hook — joined classes (everything where role is not "owner"). */
export function useJoinedBoardsFromLogin(): OwnedBoard[] {
  const rawUser = useAuthStore((state) => state.rawUser);
  return useMemo(
    () =>
      extractAllBoards(rawUser)
        .filter((b) => !isOwnerRole(b.role))
        .sort(sortByPosition),
    [rawUser],
  );
}

/** Convenience: look up any board (owned or joined) by id or code. */
export function findOwnedBoard(idOrCode: string | number): OwnedBoard | null {
  const all = extractAllBoards(useAuthStore.getState().rawUser);
  const needle = String(idOrCode).trim();
  if (!needle) return null;
  const upper = needle.toUpperCase();
  return all.find((b) => String(b.id) === needle || b.code.toUpperCase() === upper) ?? null;
}
